import {createStore} from 'vuex'
import Logger from '../utils/Logger'

import ContextService from '../services/ContextService';
import FolderService from '../services/FolderService';
import TaskService from '../services/TaskService';
import TaskTemplateService from '../services/TaskTemplateService';
import UserService from '../services/UserService';
import { createTaskSorter } from '../utils/taskSorter';

const log = Logger.create('store', { showCaller: true, showLineNumbers: true })

// Vue.use(Vuex)

let store = createStore({
  state: {
    // 20260416 -- Copilot (Claude Sonnet 4.6) -- Color maps for context and folder badge styling
    contextColorMap: {},
    folderColorMap: {},
    contexts: null,
    contextsDirty: true,
    contextsForFilter: null,
    contextsForKanban: null,
    displayProperties: null,
    folders: null,
    foldersDirty: true,
    foldersForFilter: null,
    foldersForProfile: null,
    paymentDetails: null,
    subscriptionProducts: null,
    subscriptionProductsDirty: true,
    subscriptionProductsLoading: false,
    tasks: null,
    tasksDirty: true,
    tasksLoading: true,
    taskTemplates: null,
    taskTemplatesDirty: true,
    taskTemplatesLoading: false,
    usedContexts: null,
    usedFolders: null,
    usedPriorities: null,
    usedStatuses: null,
    user: null,
    userDirty: true,
    userPrefs: {},
    userToken: null,
    userLoading: true,
  },
  
  getters: {
    // 20260416 -- Copilot (Claude Sonnet 4.6) -- Getters for context and folder color maps
    getContextColorMap: state => state.contextColorMap,
    getFolderColorMap: state => state.folderColorMap,
    getContexts: state => {
      return state.contexts
    },
    getContextsForFilter: state => {
      return state.contextsForFilter
    },
    getContextsForKanban: state => {
      return state.contextsForKanban
    },
    getFolders: state => {
      return state.folders
    },
    getFoldersForFilter: state => {
      return state.foldersForFilter
    },
    getFoldersForProfile: state => {
      return state.foldersForProfile
    },
    getPaymentDetails: state => {
      return state.paymentDetails
    },
    getSubscriptionProducts: state => {
      return state.subscriptionProducts
    },
    getSubscriptionProductsDirty: state => {
      return state.subscriptionProductsDirty
    },
    getSubscriptionProductsLoading: state => {
      return state.subscriptionProductsLoading
    },
    getTasks: state => {
      return state.tasks
    },
    getTasksLoading: state => {
      return state.tasksLoading
    },
    getTaskTemplates: state => {
      return state.taskTemplates
    },
    getTaskTemplatesDirty: state => {
      return state.taskTemplatesDirty
    },
    getTaskTemplatesLoading: state => {
      return state.taskTemplatesLoading
    },
    getUsedContexts: state => {
      return state.usedContexts
    },
    getUsedFolders: state => {
      return state.usedFolders
    },
    getUsedPriorities: state => {
      return state.usedPriorities
    },
    getUsedStatuses: state => {
      return state.usedStatuses
    },
    getUser: state => {
      return state.user
    },
    getUserDirty: state => {
      return state.userDirty
    },
    getUserPrefs: state => {
      return state.userPrefs
    },
    getUserToken: state => {
      return state.userToken
    },
    getUserLoading: state => {
      return state.userLoading
    }
  },
  mutations: {
    ADD_TASK: (state, payload) => {
      
      // 20200404 -- ZWS -- In some cases the app hasn't populated the store
      // with tasks so there's nothing to unshift.  E.g. task detail.
      // I should likely investigate a better way to handle this.
      if(typeof state.tasks != undefined && state.tasks !== null) {
        state.tasks.unshift(payload)  
      }
      
      
    },
    ADD_TASK_TEMPLATE: (state, payload) => {
      if(typeof state.taskTemplates != undefined && state.taskTemplates !== null) {
        state.taskTemplates.unshift(payload)  
      }
    },
    // 20260416 -- Copilot (Claude Sonnet 4.6) -- Mutations for context and folder color maps
    SET_CONTEXT_COLOR_MAP: (state, payload) => { state.contextColorMap = payload },
    SET_FOLDER_COLOR_MAP: (state, payload) => { state.folderColorMap = payload },
    SET_CONTEXTS: (state, payload) => {
      state.contexts = payload;
    },
    SET_CONTEXTS_DIRTY: (state, payload) => {
      state.contextsDirty = payload;
    },
    SET_CONTEXTS_FOR_FILTER: (state, payload) => {
      state.contextsForFilter = payload;
    },
    SET_CONTEXTS_FOR_KANBAN: (state, payload) => {
      state.contextsForKanban = payload;
    },
    SET_FOLDERS: (state, payload) => {
      state.folders = payload;
    },
    SET_FOLDERS_DIRTY: (state, payload) => {
      state.foldersDirty = payload;
    },
    SET_FOLDERS_FOR_FILTER: (state, payload) => {
      state.foldersForFilter = payload;
    },
    SET_FOLDERS_FOR_PROFILE: (state, payload) => {
      state.foldersForProfile = payload;
    },
    SET_PAYMENT_DETAILS: (state, payload) => {
      state.paymentDetails = payload
    },
    SET_SUBSCRIPTION_PRODUCTS: (state, payload) => {
      state.subscriptionProducts = payload
    },
    SET_SUBSCRIPTION_PRODUCTS_DIRTY: (state, payload) => {
      state.subscriptionProductsDirty = payload
    },
    SET_SUBSCRIPTION_PRODUCTS_LOADING: (state, payload) => {
      state.subscriptionProductsLoading = payload
    },
    SET_TASKS: (state, payload) => {
      //////console.log('Committing ' + payload.length + ' task(s) to the store')
      state.tasks = payload;
    },
    SET_TASKS_DIRTY: (state, payload) => {
      state.tasksDirty = payload;
    },
    SET_TASKS_LOADING: (state, payload) => {
      state.tasksLoading = payload;
    },
    SET_TASK_TEMPLATES: (state, payload) => {
      state.taskTemplates = payload
    },
    SET_TASK_TEMPLATES_DIRTY: (state, payload) => {
      state.taskTemplatesDirty = payload
    },
    SET_TASK_TEMPLATES_LOADING: (state, payload) => {
      state.taskTemplatesLoading = payload
    },
    SET_USED_CONTEXTS: (state, payload) => {
      state.usedContexts = payload;
    },
    SET_USED_FOLDERS: (state, payload) => {
      state.usedFolders = payload;
    },
    SET_USED_PRIORITIES: (state, payload) => {
      state.usedPriorities = payload;
    },
    SET_USED_STATUSES: (state, payload) => {
      state.usedStatuses = payload;
    },
    SET_USER: (state, payload) => {
      //////console.log('Store.SET_USER = ' + JSON.stringify(payload))
      state.user = payload;
    },
    SET_USER_DIRTY: (state, payload) => {
      state.userDirty = payload;
    },
    SET_USER_LOADING: (state, payload) => {
      //////console.log('Store.USER_LOADING = ' + payload)
      state.userLoading = payload
    },
    SET_USER_PREFS: (state, payload) => {
      state.userPrefs = payload
    },
    SET_USER_TOKEN: (state, payload) => {
      state.userToken = payload
    },

    
  },
  actions: {
    addTask: (context, payload) => {
      // 20191217 -- Was ADD_TASK
      context.commit('ADD_TASK', payload.task)  
    },
    addTaskTemplate: async (context, payload) => {
      
      if(typeof payload.template._id == 'undefined' || payload.template._id === null) {
			
				await TaskTemplateService.addTemplate(payload.template, context.state.userToken)
        .then((response) => {

          // console.log('store.addTaskTemplate - Result from TaskTemplateService.addTemplate: ')
          // console.log(response)

          payload.template._id = response.data.response.data._id
          payload.template.ownerUser = [{goesBy: context.state.user.goesBy, firstName: context.state.user.firstName, _id: context.state.user._id}]

          let updatedTemplates = context.state.taskTemplates
          
          updatedTemplates.push(payload.template)

          context.commit('SET_TASK_TEMPLATES', updatedTemplates)
        
        })
        .catch((e) => {
          // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log
          throw e
        })
			
			} else {
				
				// console.log('modifying existing')
				
				TaskTemplateService.updateTemplate(payload.template, context.state.userToken)
				.then((response) => {
					// console.log('Template updated: ')
					// console.log(response)
				})
			}
      
      // **********************************

      
      
      
    },
    deleteTask: async (context, payload) => {

      let idToDelete = payload
      
      // 20260129 -- Copilot (Claude Sonnet 4.5) -- Remove from local state immediately
      // WHY: Instant UI feedback while deletion happens in background
      if (context.state.tasks && Array.isArray(context.state.tasks)) {
        // Convert both IDs to strings for comparison
        const deleteIdStr = idToDelete?.toString() || idToDelete
        const updatedTasks = context.state.tasks.filter(task => {
          const taskIdStr = task._id?.toString() || task._id
          return taskIdStr !== deleteIdStr
        })
        context.commit('SET_TASKS', updatedTasks)
      }
      
      // 20260129 -- Copilot (Claude Sonnet 4.5) -- Delete from API asynchronously
      // WHY: Don't block UI, happens in background after local update
      await TaskService.deleteTask(idToDelete, context.state.userToken)
      
      // 20260129 -- Copilot (Claude Sonnet 4.5) -- Mark dirty for next load
      // WHY: Ensures future reloads get fresh data (handles edge cases)
      context.commit('SET_TASKS_DIRTY', true)

    },
    deleteTaskTemplate: (context, payload) => {

      let idToDelete = payload



      TaskTemplateService.deleteTemplate(idToDelete, context.state.userToken)
      .then((response) => {

        let updatedTemplates = []

        try {
          updatedTemplates = context.state.taskTemplates.filter(template => template._id !== idToDelete)
        } catch(e) {
          // Intentionally left blank. In some cases the store hasn't been populated
          // with templates.  Therefore there's nothing to
          // delete.  Should probably find a better way of handling this.
        }

        context.commit('SET_TASK_TEMPLATES', updatedTemplates)

      })

    },
    getContexts: async (context, payload) => {
      
      if(context.state.contextsDirty) {
        
        await context.dispatch('loadContexts', {})
        

      }

      return context.getters.getContexts
    
    },
    getContextsForFilter: async (context, payload) => {
      
      if(context.state.contextsDirty) {
        
        await context.dispatch('loadContexts', {})
        

      }

      return context.getters.getContextsForFilter
    
    },
    getFolders: async (context, payload) => {
      
      if(context.state.foldersDirty) {
        
        await context.dispatch('loadFolders', {})

      }

      return context.getters.getFolders
    
    },
    getFoldersForFilter: async (context, payload) => {
      
      if(context.state.foldersDirty) {
        
        await context.dispatch('loadFolders', {})

      }

      return context.getters.getFoldersForFilter
    
    },
    getFoldersForProfile: async (context, payload) => {
      
      if(context.state.foldersDirty) {
        
        await context.dispatch('loadFolders', {})

      }

      return context.getters.getFoldersForProfile
    
    },
    getTasks: async (context, payload) => {
      
      if(context.state.tasksDirty) {
        
        await context.dispatch('loadTasks', {})

      }

      return context.getters.getTasks
    
    },
    getTasksForGTDInbox: async (context, payload) => {
      
      if(context.state.tasksDirty) {
        
        await context.dispatch('loadTasksForGTDInbox', {})

      }

      return context.getters.getTasks
    
    },
    getTaskTemplates: async (context, payload) => {

      return context.getters.getTaskTemplates

    },
    getUser: async (context, payload) => {
      
      if(context.state.userDirty) {
        
        await context.dispatch('loadUser')

      }

      return context.getters.getUser
    
    },
    getUserPrefs: async (context, payload) => {

      if(context.state.userDirty) {
        
        await context.dispatch('loadUser')

      }

      return context.getters.getUserPrefs

    },
    loadContexts: async (context, payload) => {

      let contexts = []

      await ContextService.getContexts(context.state.user._id, true, context.state.userToken).then(function(response) {

        ////////console.log("Response from ContextService.getContexts = " + JSON.stringify(response))
        
        contexts = response.data.response.data
        //////console.log(contexts.length + " context(s) found by store.loadContexts")
        context.dispatch('setContexts', contexts)

        context.commit('SET_CONTEXTS_DIRTY', false)

      }).catch((error) => {

        //////console.log('store.loadContexts - error: ' + error)

      })
      


    },
    loadFolders: async (context, payload) => {

      let folders = []
      
      await FolderService.getFolders(context.state.user._id, true, context.state.userToken).then(function(response) {

        folders = response.data.response.data
        log.info(`Loaded ${folders.length} folder(s)`)
        context.dispatch('setFolders', folders)
        
        context.commit('SET_FOLDERS_DIRTY', false)

      }).catch((error) => {

        log.error('Error loading folders:', error)
      })
    },
    loadSubscriptionProducts: async (context, payload) => {
      
      log.debug('Loading subscription products from API...');
      
      try {
        context.commit('SET_SUBSCRIPTION_PRODUCTS_LOADING', true);
        
        // Import axios dynamically
        const axios = (await import('axios')).default;
        
        const response = await axios.get(
          '/api/v1/external/subscription-products',
          {
            headers: {
              Authorization: `Bearer ${context.state.userToken}`
            }
          }
        );
        
        if (response.data.successful) {
          log.info('Successfully loaded subscription products from API');
          context.commit('SET_SUBSCRIPTION_PRODUCTS', response.data.data);
          context.commit('SET_SUBSCRIPTION_PRODUCTS_DIRTY', false);
        } else {
          log.warn('Failed to load subscription products:', response.data.message);
        }
        
      } catch (error) {
        log.error('Error loading subscription products:', error);
      } finally {
        context.commit('SET_SUBSCRIPTION_PRODUCTS_LOADING', false);
      }
    },
    loadTasks: async (context, payload) => {
      
      return new Promise(async (resolve, reject) => {

        //////console.log('store.loadTasks')
        
        context.commit('SET_TASKS_LOADING', true)
        
        let filter = payload
        
        if((typeof filter == 'undefined') || filter === null) {
          filter = context.state.userPrefs.taskFilter
          //////console.log('filter was null, set based on data in store: ' + context.state.userPrefs.taskFilter)
        }
        
        let tasks = []
        
        await TaskService.getTasks(context.state.user._id, filter, context.state.userToken).then(function(response) {

          tasks = response.data.response.data
          
          //@TODO Clean-up.  Similar logic exists in taskOps.

          let first = context.state.userPrefs.taskSortOrder.first
          let second = context.state.userPrefs.taskSortOrder.second
          let third = context.state.userPrefs.taskSortOrder.third

          let firstDirection = context.state.userPrefs.taskSortOrder.firstDirection
          let secondDirection = context.state.userPrefs.taskSortOrder.secondDirection
          let thirdDirection = context.state.userPrefs.taskSortOrder.thirdDirection

          // console.log('Sorting by ' 
          // + first 
          // + ' (' + firstDirection + ')'
          // + ', ' + second 
          // + ' (' + secondDirection + ')'
          // + ', and then ' + third
          // + ' (' + thirdDirection + ')')

          // Refactored to use consolidated taskSorter utility for better type-safety and priority handling
          tasks.sort(createTaskSorter(first, second, third, firstDirection, secondDirection, thirdDirection))

          let contexts = []
          contexts.push('Unassigned')

          let folders = []
          folders.push('Unassigned')

          let priorities = []
          priorities.push('Unassigned')

          let statuses = []
          statuses.push('Unassigned')
          
          tasks.forEach((task, index) => {

            if(typeof task.context != 'undefined' && task.context !== null) {

              if(!contexts.includes(task.context)) {
                return contexts.push(task.context)
              }

            }

            if(typeof task.folder != 'undefined' && task.folder !== null) {

              if(!folders.includes(task.folder)) {
                return folders.push(task.folder)
              }

            }

            if(typeof task.priority != 'undefined' && task.priority !== null) {

              if(!priorities.includes(task.priority)) {
                return priorities.push(task.priority)
              }

            }

            if(typeof task.status != 'undefined' && task.status !== null) {

              if(!statuses.includes(task.status)) {
                return statuses.push(task.status)
              }

            }
            
          })

          // context.commit('SET_USED_CONTEXTS', contexts)

          // context.commit('SET_USED_FOLDERS', folders)

          // context.commit('SET_USED_PRIORITIES', priorities)

          // context.commit('SET_USED_STATUSES', statuses)
          
          context.commit('SET_TASKS', tasks)

          context.commit('SET_TASKS_LOADING', false)

          context.commit('SET_TASKS_DIRTY', false)

          resolve(response)

        }).catch((error) => {

          context.commit('SET_TASKS_LOADING', false)

          reject(error)

        })
      })
    },
    loadTasksForGTDInbox: async (context, payload) => {
      
      return new Promise(async (resolve, reject) => {

        context.commit('SET_TASKS_LOADING', true)
        
        let filter = payload
        
        if((typeof filter == 'undefined') || filter === null) {
          filter = context.state.userPrefs.taskFilter
          //////console.log('filter was null, set based on data in store: ' + context.state.userPrefs.taskFilter)
        }
        
        let tasks = []
        
        await TaskService.getTasksForGTDInbox(context.state.userToken).then(function(response) {

          // 20220528 -- ZWS -- Response content changed in v3 of the API
          if(response && response.data && response.data.response && response.data.response.data) {
            tasks = response.data.response.data
          } else {
            // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log
            tasks = response.data.data;

          }
          
          
          
          //@TODO Clean-up.  Similar logic exists in taskOps.

          let first = context.state.userPrefs.taskSortOrder.first
          let second = context.state.userPrefs.taskSortOrder.second
          let third = context.state.userPrefs.taskSortOrder.third

          let firstDirection = context.state.userPrefs.taskSortOrder.firstDirection
          let secondDirection = context.state.userPrefs.taskSortOrder.secondDirection
          let thirdDirection = context.state.userPrefs.taskSortOrder.thirdDirection
          
          // Refactored to use consolidated taskSorter utility for better type-safety and priority handling
          tasks.sort(createTaskSorter(first, second, third, firstDirection, secondDirection, thirdDirection))

          let contexts = []
          contexts.push('Unassigned')

          let folders = []
          folders.push('Unassigned')

          let priorities = []
          priorities.push('Unassigned')

          let statuses = []
          statuses.push('Unassigned')
          
          tasks.forEach((task, index) => {

            if(typeof task.context != 'undefined' && task.context !== null) {

              if(!contexts.includes(task.context)) {
                return contexts.push(task.context)
              }

            }

            if(typeof task.folder != 'undefined' && task.folder !== null) {

              if(!folders.includes(task.folder)) {
                return folders.push(task.folder)
              }

            }

            if(typeof task.priority != 'undefined' && task.priority !== null) {

              if(!priorities.includes(task.priority)) {
                return priorities.push(task.priority)
              }

            }

            if(typeof task.status != 'undefined' && task.status !== null) {

              if(!statuses.includes(task.status)) {
                return statuses.push(task.status)
              }

            }
            
          })
          
          context.commit('SET_TASKS', tasks)

          context.commit('SET_TASKS_LOADING', false)

          context.commit('SET_TASKS_DIRTY', false)

          resolve(response)

        }).catch((error) => {

          context.commit('SET_TASKS_LOADING', false)

          reject(error)

        })
      })
    },
    loadTaskTemplates: async (context, payload) => {

      context.commit('SET_TASK_TEMPLATES_LOADING', true)

      let filter = {}

      if(typeof payload != 'undefined' && payload !== null) {
        if(typeof payload.filter != 'undefined' && payload.filter !== null) {
          filter = payload.filter
        }
      }
      
      if(typeof filter.public != 'undefined' && filter.public !== null && filter.public) {
        
        await TaskTemplateService.getPublicTemplates(filter)
        .then((response) => {
          // console.log(response)

          context.commit('SET_TASK_TEMPLATES', response.data.response.data)

        })
        .catch((e) => {
          // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log
        })

        context.commit('SET_TASK_TEMPLATES_LOADING', false)


      } else {

        await TaskTemplateService.getTemplates(context.state.user._id, filter, context.state.userToken)
        .then((response) => {
          // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log
          context.commit('SET_TASK_TEMPLATES', response.data.response.data)

        })
        .catch((e) => {
          // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log
        })

        context.commit('SET_TASK_TEMPLATES_LOADING', false)

      }

      
      

      

    },
    loadUser: async (context, payload) => {

      context.commit('SET_USER_LOADING', true)
      
      await UserService.getUser(context.state.user.email, context.state.userToken).then(async function(user) {

        context.commit('SET_USER', user)

        return user

      }).then(async (user) => {
        
        await UserService.getUserPrefs(user._id, context.state.userToken).then(function(prefs) {

          let prefsTemplate = {
            defaultTaskOptions: {
              priority: null,
              status: null,
              folder: null,
              context: null,
              start: null,
              due: null
            },
            taskFilter: {
              folder: null,
              context: null,
              completed: 'N',
              quickFilter: null
            },
            taskSortOrder: {
              first: 'start', 
              second: 'due', 
              third: 'folder'
            },
          }
          
          if(!(typeof prefs == 'undefined') && !(prefs === null)) {
            
            if(typeof prefs.defaultTaskOptions == 'undefined' || prefs.defaultTaskOptions === null) {
              prefs.defaultTaskOptions = prefsTemplate.defaultTaskOptions
            }
    
            if(typeof prefs.taskFilter == 'undefined' || prefs.taskFilter === null) {
              prefs.taskFilter = prefsTemplate.taskFilter
            }
    
            if(typeof prefs.taskSortOrder == 'undefined' || prefs.taskSortOrder === null) {
              prefs.taskSortOrder = prefsTemplate.taskSortOrder
            }
    
          }
          else {
            
            //////console.log('No user preferences found in database, using system defaults.')
            prefs = prefsTemplate
          }
    
          context.commit('SET_USER_PREFS', prefs)
    
          //////console.log('userPrefs = ' + JSON.stringify(context.state.userPrefs))
    
        }).catch((error) => {
          // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log
          throw error
  
        })
      })
      .then((user) => {

        context.commit('SET_USER_LOADING', false)
    
        context.commit('SET_USER_DIRTY', false)
        
        // Load subscription products after user is loaded
        context.dispatch('loadSubscriptionProducts').catch(err => {
          log.warn('Failed to load subscription products after user load:', err);
        });

      }).catch((error) => {
  
        
        context.commit('SET_USER_LOADING', false)
        context.commit('SET_TASKS_LOADING', false)
        // 20260128 -- Copilot (Claude Sonnet 4.5) -- Removed console.log
        // WHY: Error is being thrown and will be handled by caller
          throw error
  
        })


      
      
      

      

      

    },
    setContexts: (context, payload) => {
      
      let tempList = payload

      if(!(typeof tempList == 'undefined' || tempList === null)) {

        tempList.sort(function(a, b) {
          if(a.customOrder > b.customOrder) {
            return 1
          } else if(a.customOrder < b.customOrder) {
            return -1
          } else {
            if(a.name > b.name) {
              return 1
            } else if(a.name < b.name) {
              return -1
            } else {
              return 0
            }
          }
        })

        context.commit('SET_CONTEXTS', tempList)

        let listForFilter = tempList.map(x => {
        
          return {name: x.name, item: x.name}
        
        })

        listForFilter.unshift({name: 'All', item: 'A'})
        listForFilter.unshift({name: 'Context', item: null, disabled: true})

        context.commit('SET_CONTEXTS_FOR_FILTER', listForFilter)

        let listForKanban = tempList.map(x => {
        
          return {name: x.name, item: x.name}
        
        })

        listForKanban.unshift({name: 'Unassigned', item:'unassigned'})

        context.commit('SET_CONTEXTS_FOR_KANBAN', listForKanban)

        // 20260416 -- Copilot (Claude Sonnet 4.6) -- Build context color map for badge styling
        const contextColorMap = {}
        tempList.forEach(c => { if (c.color) contextColorMap[c.name] = c.color })
        context.commit('SET_CONTEXT_COLOR_MAP', contextColorMap)

      }

    },
    setFolders: (context, payload) => {
      
      log.debug('setFolders called with', payload?.length || 0, 'folders')
      
      let tempList = payload

      if(!(typeof tempList == 'undefined' || tempList === null)) {

        tempList.sort(function(a, b) {
          if(a.customOrder > b.customOrder) {
            return 1
          } else if(a.customOrder < b.customOrder) {
            return -1
          } else {
            if(a.name > b.name) {
              return 1
            } else if(a.name < b.name) {
              return -1
            } else {
              return 0
            }
          }
        })

        context.commit('SET_FOLDERS_FOR_PROFILE', tempList)

        let list = []
        
        ////console.log('Generating listForFilter')

        try {

        
          tempList.forEach((arrayItem, index, array) => {

            let groupFound = false
            let group = null

            if(typeof arrayItem.group == 'undefined' || arrayItem.group === null || arrayItem.group == '') {
              group = 'Ungrouped'
            } else {
              group = arrayItem.group
            }

            for(let i = 0; i < list.length; i++) {
              
              if(typeof list[i].label != 'undefined' && list[i].label !== null) {
                if(list[i].label == group) {
                  list[i].options.push({text: arrayItem.name, value: arrayItem.name})
                  groupFound = true
                }
              }
            }

            if(!groupFound) {
              
              
              list.push({label: group, options:[{text: arrayItem.name, value: arrayItem.name}]})
            }

          })

        } catch(e) {
          log.error('Error generating folder list:', e);
          ////console.log('Error generating list for filter: ' + e)
        }

        // Sort by group

        list.sort(function(a, b) {
          if(a.label > b.label) {
            return 1
          } else if(a.label < b.label) {
            return -1
          } else {
            return 0
          }
        })

        ////console.log('Have listForFilter ==')

        ////console.log(listForFilter)
        
        let listForFilter = tempList.map(x => {
        
          return {name: x.name, item: x.name}
        
        })

        // listForFilter.sort(function(a, b) {
        //   if(a.label > b.label) {
        //     return 1
        //   } else if(a.label < b.label) {
        //     return -1
        //   } else {
        //     return 0
        //   }
        // })

        log.info(`Grouped ${tempList.length} folders into ${list.length} groups`)
        log.trace('Final folder structure:', list)
        
        context.commit('SET_FOLDERS', list)

        listForFilter.unshift({name: 'All', item: 'A'})

        context.commit('SET_FOLDERS_FOR_FILTER', listForFilter)

        // 20260416 -- Copilot (Claude Sonnet 4.6) -- Build folder color map for badge styling
        const folderColorMap = {}
        tempList.forEach(f => { if (f.color) folderColorMap[f.name] = f.color })
        context.commit('SET_FOLDER_COLOR_MAP', folderColorMap)

      } else {
        log.warn('setFolders: Received empty or null folder list')
      }

    },
    setTaskFilter: async(context, payload) => {

      let tempPrefs = context.state.userPrefs
      let refinedFilter = payload.refinedFilter
      
      tempPrefs.taskFilter = payload
      
      delete tempPrefs.taskFilter.refinedFilter

      //Only store user preference if a custom filter is defined.
      // if((typeof tempPrefs.quickFilter == 'undefined') || tempPrefs.quickFilter === null) {
      //   await context.dispatch('setUserPrefs', tempPrefs)
      // } else {
      //   //////console.log('Not updating userPrefs; quickFilter = ' + tempPrefs.quickFilter)
      // }
      ////console.log('store.setTaskFilter has prefs = ' + JSON.stringify(tempPrefs))
      await context.dispatch('setUserPrefs', tempPrefs)

      ////console.log('store.setTaskFilter has refinedFilter set to ' + JSON.stringify(refinedFilter))
      
      context.dispatch('loadTasks', refinedFilter)

    },
    setTaskSortOrder: (context, payload) => {
      let tempPrefs = context.state.userPrefs
      tempPrefs.taskSortOrder = payload
      context.dispatch('setUserPrefs', tempPrefs)
    },
    setTasks: (context, payload) => {
      context.commit('SET_TASKS', payload)  
    },
    
    setUserPrefs: async (context, payload) => {
      
      //////console.log('Updated userPrefs ==== ' + JSON.stringify(payload))
      
      return new Promise(async (resolve, reject) => {
      
        if(typeof payload._id == 'undefined' || payload._id === null) {
          
          payload.owner = context.state.user._id

          await UserService.createUserPrefs(payload, context.state.userToken).then(function(response) {

            payload._id = response.insertedId

            context.commit('SET_USER_PREFS', payload)

            resolve(response)


          }).catch((error) => {

            reject(error)

          })

        } else {
          
          
          
          await UserService.updateUserPrefs(payload, context.state.userToken).then(function(response) {

            context.commit('SET_USER_PREFS', payload)

            resolve(response)


          }).catch((error) => {

            reject(error)

          })
          
        }
      })
    },
    setUser(context, payload) {
      context.commit('SET_USER', payload)
      context.commit('SET_USER_DIRTY', false)
    },
    setUserLoading(context, payload) {
      context.commit('SET_USER_LOADING', payload)
    },
    setUserToken(context, payload) {
      context.commit('SET_USER_TOKEN', payload)
      // Load subscription products when token is set
      context.dispatch('loadSubscriptionProducts').catch(err => {
        log.warn('Failed to load subscription products after token set:', err);
      });
    },
    updateContexts: async (context, payload) => {
      
      await ContextService.updateContexts(payload, context.state.userToken).then(async function(response) {

        await context.dispatch('loadContexts', {})

      }).catch((error) => {

        //////console.log('store.updateContexts encountered an error: ' + error)

      })

    },
    updateFolders: async (context, payload) => {
      
      log.info('Updating folders with payload:', payload);
      
      await FolderService.updateFolders(payload, context.state.userToken).then(async function(response) {

        await context.dispatch('loadFolders', {})

      }).catch((error) => {

        log.error('store.updateFolders encountered an error:', error);  

      })

      
    },
    toggleKanban: async (context, payload) => {
      let userPrefs = context.state.userPrefs
      userPrefs.displayKanban = !userPrefs.displayKanban
      await context.dispatch('setUserPrefs', userPrefs)
    },
    updateUser: async (context, payload) => {
      
      await UserService.updateUser(payload, context.state.userToken).then(async function(response) {

        await context.dispatch('setUser', payload)

      }).catch((error) => {

        //console.log('store.updateUser encountered an error: ' + error)

      })

      
    },
  }
});

export default store;